/**
 * Single source of truth for mapping a DB message (as returned by the
 * conversations API) into the UI message shape the chat renders.
 *
 * This used to be inlined in FOUR places in MainOfficeView.jsx with diverging
 * field sets — the global-search and URL deep-link loaders dropped
 * `attachedDocuments` / `referencedExpenses` / `typedText` / `voiceSegments` /
 * `toolSteps` in different combinations, so e.g. opening a conversation from
 * global search rendered bubbles with NO attachments. Extracting one mapper
 * guarantees every load path produces the complete shape.
 *
 * DB message fields consumed:
 *   - id, content, created_at, message_type ('user' | 'assistant')
 *   - message_metadata (JSON) → repliedTo, voice, voice_segments, typed_text,
 *     toolSteps, processing_ms, attached_documents, referenced_expenses
 *
 * @param {object} msg a raw DB message
 * @returns {object} the complete UI message shape
 */
export function mapDbMessage(msg) {
    const meta = msg.message_metadata || null;
    return {
        id: msg.id,
        dbId: msg.id,
        content: msg.content,
        timestamp: new Date(msg.created_at),
        isUser: msg.message_type === 'user',
        metadata: meta,
        repliedTo: meta?.repliedTo || null,
        voice: meta?.voice || null,
        voiceSegments: meta?.voice_segments || null,
        typedText: meta?.typed_text || null,
        toolSteps: meta?.toolSteps || null,
        // #240 Main Chat Timer: the frozen Send→first-token total, persisted so a
        // reload shows the same figure (null for legacy messages → the header
        // falls back to summed per-step durations).
        processingMs: meta?.processing_ms ?? null,
        attachedDocuments: meta?.attached_documents || null,
        referencedExpenses: meta?.referenced_expenses || null,
    };
}

/**
 * Map a full conversation's `messages` array into UI shape. Returns [] when the
 * conversation has no messages.
 *
 * @param {object} conversation a conversation with an optional `messages` array
 * @returns {object[]}
 */
export function mapDbMessages(conversation) {
    if (!conversation || !Array.isArray(conversation.messages)) return [];
    return conversation.messages.map(mapDbMessage);
}

export default mapDbMessage;
